/**
 * ConnectedUsersIpcSyncManager
 *
 * Bridges the host's live connected user/admin peer count → PlayJector IPC, so the
 * PlaycastService can gate CPU/GPU affinity boosting on REAL activity.
 *
 * Why this exists: PlayJector cannot see player/admin peers — they land in the host's
 * WebRTC stack. The authoritative connected count lives here. PlayJector echoes it
 * (`hubSetConnectedUsersMSG` -> `<service>SetConnectedUsers`) and the service enables
 * affinity only while `connectedUsers > 0 || recordingActive`.
 *
 * THIS IS DELIBERATELY NOT COUPLED TO broadcast state. `broadcastStatus` reflects the
 * host's Go-Live, not consumption — an empty-but-live host must NOT keep affinity
 * boosted, which is exactly the idle host-mode heat bug this feature fixes.
 *
 * Single responsibility: listen for peer connect/disconnect and relay the aggregate
 * count over IPC. Emits only when the aggregate count actually changes.
 */

import { generators } from '@playcastdotio/Messaging';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { PeerCoordinator } from '../../peer/PeerCoordinator';

export type ConnectedUsersIpcSyncManagerDeps = {
  ipcService: IIpcService;
  peerCoordinator: PeerCoordinator;
  getHostAuthId: () => string;
};

export class ConnectedUsersIpcSyncManager {
  private unsubscribe: (() => void)[] = [];
  private currentCount = -1;

  constructor(private readonly deps: ConnectedUsersIpcSyncManagerDeps) {}

  public start(): void {
    if (this.unsubscribe.length > 0) {
      return;
    }

    const { peerCoordinator } = this.deps;

    const reflect = (): void => {
      const count =
        peerCoordinator.getPeersByType('player').length +
        peerCoordinator.getPeersByType('admin').length;
      if (count === this.currentCount) {
        return;
      }
      this.currentCount = count;

      const hostAuthId = this.deps.getHostAuthId();
      console.log(
        `[ConnectedUsersIpcSync] Reporting connected users to PlayJector: ${count}`,
      );
      this.deps.ipcService.send(
        generators.hub.setConnectedUsers(count, hostAuthId),
      );
    };

    this.unsubscribe.push(
      peerCoordinator.events.onPlayerJoined.addListener(() => reflect()),
      peerCoordinator.events.onPlayerLeft.addListener(() => reflect()),
      peerCoordinator.events.onAdminJoined.addListener(() => reflect()),
      peerCoordinator.events.onAdminLeft.addListener(() => reflect()),
    );

    reflect();
  }

  /**
   * Force a resend of the current connected-user count, ignoring the cached value.
   *
   * Used to re-synchronize after the hostAuthId is (re)assigned — the initial count may
   * have been sent with an empty auth id and would otherwise be suppressed because the
   * count did not change. Also a clean hook to re-report after a PlayJector reconnect so
   * the service re-applies affinity even when the peer set is unchanged.
   */
  public resync(): void {
    this.currentCount = -1;
    const { peerCoordinator } = this.deps;
    const count =
      peerCoordinator.getPeersByType('player').length +
      peerCoordinator.getPeersByType('admin').length;
    this.currentCount = count;
    const hostAuthId = this.deps.getHostAuthId();
    console.log(
      `[ConnectedUsersIpcSync] Resyncing connected users to PlayJector: ${count}`,
    );
    this.deps.ipcService.send(
      generators.hub.setConnectedUsers(count, hostAuthId),
    );
  }

  public stop(): void {
    for (const unsubscribe of this.unsubscribe) {
      unsubscribe();
    }
    this.unsubscribe = [];
    this.currentCount = -1;
  }
}